import { useRef, useState } from 'react';
import { useApp } from '../store';
import type { Doc, AarContent, AarSustainLine } from '../types';
import {
  AAR_SECTIONS, AAR_SUSTAIN_EMPTY, lessonFromAarLine, emptySustainLine, filedLessonFromLine,
  scheduleVarianceLine, scheduleVarianceContext, costVarianceLine, costVarianceContext,
  AAR_BENEFIT_STATUSES, AAR_BENEFIT_STATUS_LABELS, AAR_BENEFIT_STATUS_RAG, emptyBenefitLine,
} from '../aar';
import type { AarBenefitLine, AarBenefitStatus } from '../types';
import { AAR_LINE_KINDS, AAR_LINE_KIND_LABELS, LESSON_KIND_LABELS, hasRecommendation, lessonsForBoard } from '../lessons';
import { richText } from '../rich-text';
import { aarPdf } from '../aar-pdf';
import { displayName } from '../people';
import { approvedCopy, type SignatureRow } from '../change-request-pdf';
import { asDate, asTime } from '../change-request';
import { DOCUMENT_STATE } from '../documents';
import RichTextEditor, { EMPTY_RICH_DOC, type RichDoc } from './RichTextEditor';
import Icon from './Icon';
import { LessonForm } from './LessonsView';

/**
 * THE AFTER-ACTION REPORT EDITOR — 3.19 Learning & Governance spine, A1.
 *
 * The closing retrospective, opened in Documents the way the change-request and
 * charter editors are. Its four sections (AAR_SECTIONS) are rich text — the same
 * RichTextEditor the charter uses — stored on `doc.aar` (AarContent). The first
 * three are composed from the board at creation (shown with a "From the board"
 * tag) and then edited; the fourth, "what to sustain and improve", is the
 * person's own: each line carries a kind, and "Save as a lesson" opens the
 * Lessons add-form PRE-FILLED (the person completes the "next time" recommendation
 * and confirms), after which the line shows as filed.
 *
 * Saving follows the charter's pattern: edits are debounced and written whole to
 * `doc.aar` via `saveDocument`. To be SIGNED it is produced to a PDF (aarPdf) and
 * uploaded through the ordinary document path, exactly as the charter and the
 * requirements document are — and once signed, the approved copy (the signed file
 * plus a Record of Approval page) can be downloaded. The editor only renders its
 * sections and foot; the document's title, state chip and the file sit in the
 * shared Documents chrome around it.
 */

const EMPTY_AAR: AarContent = {
  supposed: EMPTY_RICH_DOC,
  actual: EMPTY_RICH_DOC,
  why: EMPTY_RICH_DOC,
  sustain: [],
};

// The kind badge shares the Lessons register's own tint classes (.gov .kind), so
// a line reads the same here as it will in the register once filed.
const KIND_CLASS: Record<AarSustainLine['kind'], string> = {
  worked: 'worked',
  didnt: 'didnt',
  recommendation: 'rec',
};

export function AfterActionReport({
  doc,
  mayEdit,
  onProblem,
}: {
  doc: Doc;
  mayEdit: boolean;
  onProblem: (problem: string) => void;
}) {
  const saveDocument = useApp((s) => s.saveDocument);
  const uploadDocumentFile = useApp((s) => s.uploadDocumentFile);
  const documentFileUrl = useApp((s) => s.documentFileUrl);
  const projectLogoUrl = useApp((s) => s.projectLogoUrl);
  const recordAudit = useApp((s) => s.recordAudit);
  const lessons = useApp((s) => s.lessons);
  const documents = useApp((s) => s.documents);
  const revealLink = useApp((s) => s.revealLink);
  const signoffs = useApp((s) => s.signoffs);
  const user = useApp((s) => s.user);
  const profiles = useApp((s) => s.profiles);
  const meta = useApp((s) => s.meta);
  const projectId = useApp((s) => s.projectId);
  const projectName = meta.name;

  const [content, setContent] = useState<AarContent>(doc.aar ?? EMPTY_AAR);
  // The sustain line whose "Save as a lesson" form is open, if any.
  const [lessonFor, setLessonFor] = useState<AarSustainLine | null>(null);
  const [making, setMaking] = useState(false);

  // Debounced whole-document save, like the charter: `latest` is the freshest
  // content (so rapid edits across sections compose), written a beat after typing
  // stops. Editable rich editors own their own content after mount, so updating
  // `content` on every keystroke never fights the caret — it just keeps the
  // per-line kind, the disabled states and the filed-detection honest.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef<AarContent>(content);
  const persist = (next: AarContent) => {
    latest.current = next;
    setContent(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      onProblem(await saveDocument(doc.id, { aar: latest.current }));
    }, 800);
  };

  const setSection = (key: 'supposed' | 'actual' | 'why', d: RichDoc) =>
    persist({ ...latest.current, [key]: d });
  const setWhy = (key: 'scheduleWhy' | 'costWhy', d: RichDoc) =>
    persist({ ...latest.current, [key]: d });
  const patchLine = (id: string, patch: Partial<AarSustainLine>) =>
    persist({
      ...latest.current,
      sustain: latest.current.sustain.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    });
  const addLine = () =>
    persist({ ...latest.current, sustain: [...latest.current.sustain, emptySustainLine(crypto.randomUUID())] });
  const removeLine = (id: string) =>
    persist({ ...latest.current, sustain: latest.current.sustain.filter((l) => l.id !== id) });

  // The benefits list — seeded from the charter, then set where each stands.
  const benefits = content.benefits ?? [];
  const patchBenefit = (id: string, patch: Partial<AarBenefitLine>) =>
    persist({ ...latest.current, benefits: (latest.current.benefits ?? []).map((b) => (b.id === id ? { ...b, ...patch } : b)) });
  const addBenefit = () =>
    persist({ ...latest.current, benefits: [...(latest.current.benefits ?? []), emptyBenefitLine(crypto.randomUUID())] });
  const removeBenefit = (id: string) =>
    persist({ ...latest.current, benefits: (latest.current.benefits ?? []).filter((b) => b.id !== id) });
  // The charter this project's benefits were seeded from — the "From the charter"
  // tag reveals it so the author can edit the source.
  const charterDoc = documents.find((d) => d.kind === 'charter');

  // A line is filed when a live lesson links back to THIS report with the same
  // one-line situation — the shared derive-first dedupe (filedLessonFromLine), so
  // the done-state survives a reload and a second save never duplicates.
  const filedLessonFor = (line: AarSustainLine) => filedLessonFromLine(lessons, line, doc.id);

  // The lessons already captured on this project DURING it — so the closure
  // report CONSOLIDATES what the project learned rather than starting empty. Shown
  // read-only above the sustain editor; the sustain section is for anything else.
  const boardLessons = lessonsForBoard(lessons, projectId);

  // The customer's mark, fetched as bytes so the PDF reaches for nothing (R18).
  const theLogo = async () => {
    if (!meta.logoPath) return undefined;
    try {
      const address = await projectLogoUrl();
      if (!address) return undefined;
      return {
        bytes: await (await fetch(address)).arrayBuffer(),
        type: (meta.logoType === 'jpg' ? 'jpg' : 'png') as 'png' | 'jpg',
      };
    } catch {
      return undefined;
    }
  };

  // Who signed, frozen onto the document once it was sent — the same rows the
  // Sign-offs page reads. The Record of Approval page is drawn from these.
  const rows = signoffs.filter((r) => r.documentId === doc.id).sort((a, b) => a.seq - b.seq);
  const signatures: SignatureRow[] = rows.map((r) => ({
    title: r.position ?? '',
    name: r.name ?? '',
    how: !r.signedAt
      ? ''
      : r.answer === 'rejected'
        ? 'Rejected'
        : r.onBehalfBy
          ? `Approved on behalf by ${r.onBehalfBy}`
          : r.answeredVia === 'link'
            ? 'Approved by link'
            : 'Approved in FlightDeck',
    signedOn: r.signedAt ? asDate(r.signedAt) : '',
    signedTime: r.signedAt ? asTime(r.signedAt) : '',
  }));

  // PRODUCE THE PDF — the report becomes the fixed file the sign-off engine signs.
  // Save the latest edit first so the file matches what is on screen, then render,
  // wrap in a File and upload through the ordinary (fingerprinting) path. Only ever
  // on a draft, so both writes are allowed.
  const produce = async () => {
    setMaking(true);
    try {
      clearTimeout(timer.current);
      const snapshot = latest.current;
      const saveErr = await saveDocument(doc.id, { aar: snapshot });
      if (saveErr) throw new Error(saveErr);
      const bytes = await aarPdf(
        {
          title: doc.title,
          board: projectName,
          preparedOn: asDate(doc.createdAt),
          preparedBy: user?.email ? displayName(user.email, profiles) : '',
          status: DOCUMENT_STATE[doc.state],
          logo: await theLogo(),
          // The lessons captured during this project, consolidated into the record
          // (the screen shows them above the sustain editor; the signed PDF carries
          // them too). A point-in-time snapshot at produce time.
          lessons: boardLessons,
        },
        snapshot,
      );
      const name = `${doc.title.replace(/[^\w\-.]+/g, ' ').replace(/\s+/g, ' ').trim() || 'After-action report'}.pdf`;
      const file = new File([bytes as BlobPart], name, { type: 'application/pdf' });
      onProblem(await uploadDocumentFile(doc.id, file));
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The after-action report PDF could not be produced.');
    }
    setMaking(false);
  };

  // THE APPROVED COPY — the signed report with a Record of Approval page added,
  // drawn from the electronic sign-off. A download, never a replacement: the signed
  // file is the one people signed and must not change (#305).
  const downloadApproved = async () => {
    setMaking(true);
    try {
      const address = await documentFileUrl(doc.id);
      if (!address) throw new Error('The signed file could not be found.');
      const bytes = await approvedCopy(
        await (await fetch(address)).arrayBuffer(),
        {
          title: doc.title,
          board: projectName,
          sprint: 'The whole project',
          raisedOn: asDate(doc.createdAt),
          version: String(doc.version ?? 1),
          fileHash: doc.fileHash,
          logo: await theLogo(),
        },
        signatures,
      );
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(doc.fileName ?? 'After-action report.pdf').replace(/\.pdf$/i, '')} — approved.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      void recordAudit('project', `downloaded the approved copy of "${doc.title}"`);
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The approved copy could not be made.');
    }
    setMaking(false);
  };

  // Save-as-a-lesson opens the add-form pre-filled; the freshest line text comes
  // from `latest` (the editor owns it), not the possibly-older render value.
  if (lessonFor) {
    const live = latest.current.sustain.find((l) => l.id === lessonFor.id) ?? lessonFor;
    const draft = lessonFromAarLine(live, { boardId: projectId, doc });
    return (
      <div className="overlay aar-lesson-overlay" role="dialog" aria-modal="true" aria-label="Save as a lesson">
        <div className="aar-lesson-panel">
          <LessonForm
            mode="add"
            contextName={projectName}
            boardId={projectId}
            initial={{
              title: draft.title,
              kind: draft.kind,
              category: draft.category,
              body: draft.body,
              recommendation: draft.recommendation,
              links: draft.links,
            }}
            onClose={() => setLessonFor(null)}
          />
        </div>
      </div>
    );
  }

  const canDownload = doc.state === 'signed' && signatures.some((s) => s.signedOn);

  return (
    <div className="gov aar-doc">
      {AAR_SECTIONS.map((section) => (
        <section className="fd-sec aar-sec" key={section.key}>
          <div className="aar-sec-head">
            <h3 className="fd-sec-h">{section.heading}</h3>
            {section.key === 'benefits' ? (
              charterDoc && (
                <button
                  type="button"
                  className="aar-fromboard aar-fromcharter"
                  onClick={() => revealLink({ kind: 'document', id: charterDoc.id })}
                  title="Open the charter"
                >
                  <Icon name="arrow" size={13} />
                  From the charter
                </button>
              )
            ) : (
              section.prefilled && (
                <span className="aar-fromboard">
                  <Icon name="arrow" size={13} />
                  From the board
                </span>
              )
            )}
          </div>
          <p className="aar-sec-help">{section.help}</p>

          {section.key === 'benefits' ? (
            <div className="aar-benefits">
              {benefits.length === 0 && !mayEdit && (
                <p className="aar-sustain-none">No benefits recorded yet.</p>
              )}
              {benefits.map((b) => (
                <div className="aar-benefit" key={b.id}>
                  <div className="aar-benefit-main">
                    <label className="fd-lab">
                      Benefit<span className="req">*</span>
                    </label>
                    <RichTextEditor
                      value={b.statement}
                      editable={mayEdit}
                      onChange={(d) => patchBenefit(b.id, { statement: d })}
                      ariaLabel="Benefit"
                      ariaRequired
                      placeholder="The value this project was meant to deliver."
                    />
                  </div>
                  <div className="aar-benefit-side">
                    <div className="aar-benefit-field">
                      <label className="fd-lab">Where it stands</label>
                      {mayEdit ? (
                        <select
                          className="input"
                          value={b.status}
                          aria-label="Where this benefit stands"
                          onChange={(e) => patchBenefit(b.id, { status: e.target.value as AarBenefitStatus })}
                        >
                          {AAR_BENEFIT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {AAR_BENEFIT_STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`rag ${AAR_BENEFIT_STATUS_RAG[b.status]}`}>
                          <span className="dot" />
                          {AAR_BENEFIT_STATUS_LABELS[b.status]}
                        </span>
                      )}
                    </div>
                    <div className="aar-benefit-field">
                      <label className="fd-lab">
                        Tracked after close by<span className="req">*</span>
                      </label>
                      {mayEdit ? (
                        <input
                          className="input"
                          aria-label="Tracked after close by"
                          aria-required
                          value={b.monitor ?? ''}
                          onChange={(e) => patchBenefit(b.id, { monitor: e.target.value })}
                          placeholder="Name or role"
                        />
                      ) : (
                        <span className="aar-benefit-monitor">{b.monitor || '—'}</span>
                      )}
                    </div>
                    {mayEdit && (
                      <button
                        type="button"
                        className="aar-line-del"
                        aria-label="Remove this benefit"
                        onClick={() => removeBenefit(b.id)}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {mayEdit && (
                <button type="button" className="aar-add-line" onClick={addBenefit}>
                  <Icon name="plus" size={14} />
                  Add a benefit
                </button>
              )}
            </div>
          ) : section.key !== 'sustain' ? (
            <>
              <RichTextEditor
                value={content[section.key as 'supposed' | 'actual' | 'why']}
                editable={mayEdit}
                onChange={(d) => setSection(section.key as 'supposed' | 'actual' | 'why', d)}
                ariaLabel={section.heading}
              />
              {section.key === 'actual' && (scheduleVarianceLine(content.variance) || costVarianceLine(content.variance)) && (
                <div className="aar-var">
                  <p className="aar-var-h">The reasons behind the variance</p>
                  <p className="aar-var-help">
                    Say why the project ended where it did — the figure is already reported above; this is the explanation a reader needs.
                  </p>
                  {scheduleVarianceLine(content.variance) && (
                    <div className="aar-var-row">
                      <div className="aar-var-fig">
                        <span className="aar-var-mark" aria-hidden>▲</span>
                        <span className="aar-var-num">{scheduleVarianceLine(content.variance)}</span>
                        <span className="aar-var-ctx">{scheduleVarianceContext(content.variance)}</span>
                      </div>
                      <label className="fd-lab">
                        Why the schedule moved<span className="req">*</span>
                      </label>
                      <RichTextEditor
                        value={content.scheduleWhy ?? EMPTY_RICH_DOC}
                        editable={mayEdit}
                        onChange={(d) => setWhy('scheduleWhy', d)}
                        ariaLabel="Why the schedule moved"
                        ariaRequired
                        placeholder="What pushed the Go-Live out — one or two lines."
                      />
                    </div>
                  )}
                  {costVarianceLine(content.variance) && (
                    <div className="aar-var-row">
                      <div className="aar-var-fig">
                        <span className="aar-var-mark" aria-hidden>▲</span>
                        <span className="aar-var-num">{costVarianceLine(content.variance)}</span>
                        <span className="aar-var-ctx">{costVarianceContext(content.variance)}</span>
                      </div>
                      <label className="fd-lab">
                        Why the cost moved<span className="req">*</span>
                      </label>
                      <RichTextEditor
                        value={content.costWhy ?? EMPTY_RICH_DOC}
                        editable={mayEdit}
                        onChange={(d) => setWhy('costWhy', d)}
                        ariaLabel="Why the cost moved"
                        ariaRequired
                        placeholder="What drove the overrun — one or two lines."
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="aar-sustain">
              {boardLessons.length > 0 && (
                <div className="aar-captured">
                  <p className="aar-captured-h">
                    Captured during this project
                    <span className="aar-captured-n">{boardLessons.length}</span>
                  </p>
                  <p className="aar-captured-help">
                    Lessons already logged on this project — part of the record. Add anything else below.
                  </p>
                  <div className="aar-captured-list">
                    {boardLessons.map((l) => (
                      <button
                        type="button"
                        className="aar-captured-row"
                        key={l.id}
                        onClick={() => revealLink({ kind: 'lesson', id: l.id })}
                        title="Open in the Lessons register"
                      >
                        <span className={`kind ${KIND_CLASS[l.kind]}`}>
                          <span className="dot" />
                          {LESSON_KIND_LABELS[l.kind]}
                        </span>
                        <span className="aar-captured-title">{l.title}</span>
                        {hasRecommendation(l) && (
                          <span className="rec-badge">
                            <Icon name="sparkle" size={12} /> Recommendation
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {content.sustain.length === 0 && !mayEdit && (
                <p className="aar-sustain-none">{AAR_SUSTAIN_EMPTY}</p>
              )}
              {content.sustain.map((line) => {
                const filed = filedLessonFor(line);
                const said = richText(line.text).trim();
                return (
                  <div className="aar-line" key={line.id}>
                    <div className="aar-line-text">
                      <RichTextEditor
                        value={line.text}
                        editable={mayEdit && !filed}
                        onChange={(d) => patchLine(line.id, { text: d })}
                        ariaLabel="What to sustain or improve"
                      />
                    </div>
                    {filed ? (
                      <div className="aar-line-filed">
                        <span className={`kind ${KIND_CLASS[line.kind]}`}>
                          <span className="dot" />
                          {AAR_LINE_KIND_LABELS[line.kind]}
                        </span>
                        <span className="seed-done">
                          <Icon name="check" size={14} />
                          Saved to lessons
                        </span>
                      </div>
                    ) : (
                      mayEdit && (
                        <div className="aar-line-acts">
                          <select
                            className="ctl aar-kind"
                            value={line.kind}
                            aria-label="What kind of lesson this line is"
                            onChange={(e) => patchLine(line.id, { kind: e.target.value as AarSustainLine['kind'] })}
                          >
                            {AAR_LINE_KINDS.map((k) => (
                              <option key={k} value={k}>
                                {AAR_LINE_KIND_LABELS[k]}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-small"
                            disabled={!said}
                            onClick={() => setLessonFor(line)}
                          >
                            <Icon name="arrow" size={13} />
                            Save as a lesson
                          </button>
                          <button
                            type="button"
                            className="aar-line-del"
                            aria-label="Remove this line"
                            onClick={() => removeLine(line.id)}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
              {mayEdit && (
                <button type="button" className="aar-add-line" onClick={addLine}>
                  <Icon name="plus" size={14} />
                  Add a line
                </button>
              )}
            </div>
          )}
        </section>
      ))}

      {mayEdit ? (
        <div className="aar-foot">
          <button className="btn btn-primary btn-small" disabled={making} onClick={produce}>
            {making ? 'Producing…' : doc.filePath ? 'Produce it again' : 'Produce the PDF'}
          </button>
          <span className="hint">Makes the file that gets signed off. Nothing can be sent for sign-off without it.</span>
        </div>
      ) : (
        canDownload && (
          <div className="aar-foot">
            <button className="btn btn-small" disabled={making} onClick={downloadApproved}>
              {making ? 'Preparing…' : 'Download approved copy'}
            </button>
            <span className="hint">The signed report, with the Record of Approval page.</span>
          </div>
        )
      )}
    </div>
  );
}
